import { useRef, useEffect } from 'react';

import { cn } from 'src/lib/utils';

type ISoftwareOnboardingHeader = {
  justify?: 'between' | 'end';
};

const SoftwareOnboardingHeader = ({
  children,
  justify = 'between',
}: React.PropsWithChildren<ISoftwareOnboardingHeader>) => {
  return (
    <header
      className={cn(
        'shrink-0 flex justify-between gap-4 px-6 pt-3 md:pt-6 md:px-10',
        justify === 'end' && 'justify-end',
      )}
    >
      {children}
    </header>
  );
};

type ISoftwareOnboardingMain = {
  subtitle?: string;
  subtitle2?: string;
  title: string;
  description?: string;
  noteText?: string | React.ReactNode;
  notePrefix?: string;
  className?: string;
  classes?: {
    header?: string;
    note?: string;
    wrapper?: string;
    subtitle?: string;
  };
};

const SoftwareOnboardingMain = ({
  title,
  subtitle,
  subtitle2,
  description,
  noteText,
  notePrefix = 'Note:',
  className,
  children,
  classes: { header, note, wrapper, subtitle: subtitleClass } = {},
}: React.PropsWithChildren<ISoftwareOnboardingMain>) => {
  const mainRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const main = mainRef.current;

    if (!main) return;

    const checkScroll = () => {
      const hasScroll = main.scrollHeight > main.clientHeight;
      main.classList.toggle('is-scrolling', hasScroll);
    };

    // Check on mount and when content changes
    checkScroll();
    const observer = new ResizeObserver(checkScroll);
    observer.observe(main);

    return () => observer.disconnect();
  }, []);

  return (
    <main ref={mainRef} className={cn('relative grow flex flex-col overflow-y-auto min-h-0', className)}>
      <div className={cn('flex flex-col py-10 px-6 md:py-16 md:px-10 my-auto', wrapper)}>
        <div className={cn('container mx-auto space-y-4 mb-10', header)}>
          {subtitle2 && <h2 className="text-2xl text-foreground font-medium mb-2 leading-none">{subtitle2}</h2>}
          {subtitle && <h2 className={cn('text-2xl text-secondary-foreground mb-2', subtitleClass)}>{subtitle}</h2>}
          <h1 className="text-3xl text-foreground">{title}</h1>
          {description && <p className="text-base font-semibold text-foreground/80">{description}</p>}
          {noteText && (
            <p
              className={cn(
                'italic text-base text-muted-foreground/80 whitespace-pre-wrap [&>a]:text-muted-foreground/80!',
                note,
              )}
            >
              {notePrefix && <span className="not-italic">{notePrefix} </span>}
              {noteText}
            </p>
          )}
        </div>
        {children}
      </div>
    </main>
  );
};

const SoftwareOnboardingLayout = ({ children }: React.PropsWithChildren) => {
  return <div className="flex flex-col h-screen bg-slate-50">{children}</div>;
};

type ISoftwareOnboardingFooter = {
  justify?: 'between' | 'end';
};

const SoftwareOnboardingFooter = ({
  children,
  justify = 'between',
}: React.PropsWithChildren<ISoftwareOnboardingFooter>) => {
  return (
    <footer
      className={cn(
        'flex justify-between shrink-0 px-6 pb-3 md:px-10 md:pb-6 gap-2',
        justify === 'end' && 'justify-end',
      )}
    >
      {children}
    </footer>
  );
};

export { SoftwareOnboardingLayout, SoftwareOnboardingHeader, SoftwareOnboardingFooter, SoftwareOnboardingMain };
